import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

interface ErrorSnackbarContextValue {
  showError: (message: string) => void;
}

const ErrorSnackbarContext = createContext<ErrorSnackbarContextValue | null>(null);

interface ErrorSnackbarState {
  open: boolean;
  message: string;
}

/**
 * ErrorSnackbarProvider — context provider that surfaces API/runtime errors to the user.
 *
 * Wrap the app (or a subtree) with this provider. Child components call useErrorSnackbar()
 * to get the showError function. Errors appear as a bottom-left MUI Snackbar with severity
 * "error" and auto-dismiss after 6 seconds.
 */
export function ErrorSnackbarProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ErrorSnackbarState>({ open: false, message: '' });

  const showError = useCallback((message: string): void => {
    setState({ open: true, message });
  }, []);

  function handleClose(): void {
    setState((prev) => ({ ...prev, open: false }));
  }

  return (
    <ErrorSnackbarContext.Provider value={{ showError }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleClose} severity="error" variant="filled" sx={{ width: '100%' }}>
          {state.message}
        </Alert>
      </Snackbar>
    </ErrorSnackbarContext.Provider>
  );
}

/**
 * useErrorSnackbar — returns { showError } from the nearest ErrorSnackbarProvider.
 *
 * Throws if called outside of an ErrorSnackbarProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook and provider are intentionally co-located
export function useErrorSnackbar(): ErrorSnackbarContextValue {
  const ctx = useContext(ErrorSnackbarContext);
  if (!ctx) {
    throw new Error('useErrorSnackbar must be used within an ErrorSnackbarProvider');
  }
  return ctx;
}
